import type { TSingleTypeResponse, TTenderhub } from "../types/tenderhub";

import type { TContactForm } from "../types/types";

const API_URL = "https://cms.creon-cg.com/api/";
const API_URL_CMS =
  process.env.REACT_APP_API_URL_CMS || "https://api.creon-cg.com/";
const ASSETS_URL = "https://cms.creon-cg.com/";

export function getAssetUrl(path?: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (ASSETS_URL || "").replace(/\/$/, "");
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

type RequestOptions = {
  signal?: AbortSignal;
};

async function readCmsErrorMessage(response: Response): Promise<string> {
  const fallback = `CMS API error: ${response.status}${
    response.statusText ? ` ${response.statusText}` : ""
  }`;
  const ct = response.headers.get("content-type");
  if (!ct?.includes("application/json")) {
    return fallback;
  }
  try {
    const data = (await response.json()) as {
      error?: string;
      message?: string;
    };
    if (typeof data?.error === "string" && data.error.trim() !== "") {
      return data.error;
    }
    if (typeof data?.message === "string" && data.message.trim() !== "") {
      return data.message;
    }
  } catch {
    return fallback;
  }
  return fallback;
}

async function apiGet(path: string, options: RequestOptions = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.statusText}`);
  }
  return response.json();
}

async function apiPostCms(
  path: string,
  body: unknown,
  options: RequestOptions = {},
) {
  if (!API_URL_CMS) {
    throw new Error("REACT_APP_API_URL_CMS is not defined");
  }

  const isFormData =
    typeof FormData !== "undefined" && body instanceof FormData;

  const headers: HeadersInit = {};
  let payload: BodyInit;

  if (isFormData) {
    payload = body as FormData;
  } else {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const response = await fetch(`${API_URL_CMS}${path}`, {
    method: "POST",
    headers,
    body: payload,
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(await readCmsErrorMessage(response));
  }

  return response.json();
}

export async function getCasesApi(options?: RequestOptions) {
  return apiGet("cases", options);
}

export async function getCasesByTagsApi(
  tags: string[],
  options?: RequestOptions,
) {
  return apiGet(`cases?case-tag=${tags.join(",")}`, options);
}
export async function getCasesByDirectionFilterApi(
  directionFilter: string,
  options?: RequestOptions,
) {
  return apiGet(`cases?direction-filter=${directionFilter}`, options);
}

export async function getCaseBySlugApi(slug: string, options?: RequestOptions) {
  return apiGet(`cases/slug/${slug}`, options);
}

export async function getCaseTagsApi(options?: RequestOptions) {
  return apiGet("case-tags", options);
}

export async function getMainPageApi(options?: RequestOptions) {
  return apiGet("main-page", options);
}

export async function getTenderhubApi(): Promise<TSingleTypeResponse<TTenderhub>> {
  return apiGet("tenderhub");
}
export async function getDirectionsApi(options?: RequestOptions) {
  return apiGet("direction-filters", options);
}

export async function postContactApi(
  data: FormData | TContactForm,
  options?: RequestOptions,
) {
  return apiPostCms("api/contact", data, options);
}

export async function postRegistrationApi(
  formData: FormData,
  options?: RequestOptions,
) {
  return apiPostCms("api/registration", formData, options);
}
